import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLang } from "../context/LangContext";
import "../styles/pages/UseCases.css";

const AUTO_ROTATE_SPEED = 0.1;
const RESUME_DELAY = 1800;

export default function UseCases() {
  const { dict } = useLang();
  const useCasesDict = useMemo(() => dict?.useCases ?? {}, [dict]);

  const containerRef = useRef(null);
  const carouselContainerRef = useRef(null);
  const carouselRef = useRef(null);

  const isPointerDownRef = useRef(false);
  const pointerStartedInsideRef = useRef(false);
  const currentPointerPosRef = useRef(0);
  const lastPointerPosRef = useRef(0);
  const moveToRef = useRef(0);
  const lastMoveToRef = useRef(0);
  const autoRotateRef = useRef(true);
  const rafRef = useRef(null);
  const resumeTimerRef = useRef(null);

  const cards = useMemo(() => {
    const items = Array.isArray(useCasesDict?.items) ? useCasesDict.items : [];

    return items
      .filter((item) => {
        const title = (item?.title ?? "").trim().toLowerCase();
        return title !== "operasyon senaryosu";
      })
      .map((item, index) => ({
        id: item?.id ?? `usecase-${index}`,
        title: item?.title ?? "",
        text: item?.text ?? "",
        image: item?.image ?? item?.img ?? item?.cover ?? "",
        alt: item?.alt ?? item?.title ?? `Use case ${index + 1}`,
      }));
  }, [useCasesDict]);

  const lerp = useCallback((a, b, n) => (1 - n) * a + n * b, []);

  const distanceZ = useCallback((widthElement, length, gap) => {
    return widthElement / 2 / Math.tan(Math.PI / length) + gap;
  }, []);

  const calculateHeight = useCallback((z) => {
    const t = Math.atan((90 * Math.PI) / 180 / 2);
    return t * 2 * z;
  }, []);

  const calculateFov = useCallback((carouselProps) => {
    if (!carouselContainerRef.current) return 0;

    const perspective = parseFloat(
      window.getComputedStyle(carouselContainerRef.current).perspective
    );

    const length =
      Math.sqrt(carouselProps.w * carouselProps.w) +
      Math.sqrt(carouselProps.h * carouselProps.h);

    return 2 * Math.atan(length / (2 * perspective)) * (180 / Math.PI);
  }, []);

  const updateDepthEffect = useCallback(() => {
    const carouselEl = carouselRef.current;
    if (!carouselEl) return;

    const items = carouselEl.querySelectorAll(".usecases-carousel-item");
    const itemCount = items.length;

    if (!itemCount) return;

    items.forEach((item, index) => {
      const itemAngle = (360 / itemCount) * index + lastMoveToRef.current;
      const normalized = ((itemAngle % 360) + 360) % 360;
      const frontFactor = Math.cos((normalized * Math.PI) / 180);

      const opacity = 0.48 + ((frontFactor + 1) / 2) * 0.52;
      const scale = 0.92 + ((frontFactor + 1) / 2) * 0.08;
      const brightness = 0.84 + ((frontFactor + 1) / 2) * 0.16;

      item.style.opacity = `${opacity}`;
      item.style.transform = `rotateY(${
        (360 / itemCount) * index
      }deg) translateZ(var(--tz)) scale(${scale})`;
      item.style.filter = `brightness(${brightness})`;
      item.style.pointerEvents = frontFactor > 0.35 ? "auto" : "none";
    });
  }, []);

  const createCarousel = useCallback(() => {
    const containerEl = containerRef.current;
    const carouselContainerEl = carouselContainerRef.current;
    const carouselEl = carouselRef.current;

    if (!containerEl || !carouselContainerEl || !carouselEl) return;

    const items = carouselEl.querySelectorAll(".usecases-carousel-item");
    const itemCount = items.length;

    if (!itemCount) return;

    const rect = carouselContainerEl.getBoundingClientRect();
    const carouselProps = {
      w: rect.width,
      h: rect.height,
    };

    const degrees = 360 / itemCount;

    let gap = 34;
    if (window.innerWidth < 1280) gap = 26;
    if (window.innerWidth < 768) gap = 16;
    if (window.innerWidth < 480) gap = 10;

    const tz = distanceZ(carouselProps.w, itemCount, gap);
    calculateFov(carouselProps);

    const height = Math.max(calculateHeight(tz), carouselProps.h + 40);

    containerEl.style.width = `${Math.min(
      tz * 2 + gap * itemCount,
      window.innerWidth
    )}px`;
    containerEl.style.height = `${height}px`;

    carouselContainerEl.style.perspective =
      window.innerWidth < 768 ? "1000px" : "1500px";

    items.forEach((item, index) => {
      item.style.setProperty("--rotatey", `${degrees * index}deg`);
      item.style.setProperty("--tz", `${tz}px`);
    });

    carouselEl.style.setProperty("--rotatey", `${lastMoveToRef.current}deg`);
    updateDepthEffect();
  }, [calculateFov, calculateHeight, distanceZ, updateDepthEffect]);

  const getPosX = useCallback((x) => {
    currentPointerPosRef.current = x;
    const difference = currentPointerPosRef.current - lastPointerPosRef.current;

    moveToRef.current += difference * 0.24;
    lastPointerPosRef.current = currentPointerPosRef.current;
  }, []);

  const startInteraction = useCallback((x) => {
    const carouselEl = carouselRef.current;
    if (!carouselEl) return;

    isPointerDownRef.current = true;
    pointerStartedInsideRef.current = true;
    autoRotateRef.current = false;
    currentPointerPosRef.current = x;
    lastPointerPosRef.current = x;
    carouselEl.style.cursor = "grabbing";

    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }
  }, []);

  const endInteraction = useCallback(() => {
    const carouselEl = carouselRef.current;
    if (!carouselEl) return;

    isPointerDownRef.current = false;
    pointerStartedInsideRef.current = false;
    carouselEl.style.cursor = "grab";

    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }

    resumeTimerRef.current = setTimeout(() => {
      autoRotateRef.current = true;
    }, RESUME_DELAY);
  }, []);

  const animate = useCallback(() => {
    const carouselEl = carouselRef.current;
    if (!carouselEl) return;

    if (autoRotateRef.current && !isPointerDownRef.current) {
      moveToRef.current += AUTO_ROTATE_SPEED;
    }

    lastMoveToRef.current = lerp(lastMoveToRef.current, moveToRef.current, 0.08);
    carouselEl.style.setProperty("--rotatey", `${lastMoveToRef.current}deg`);

    updateDepthEffect();
    rafRef.current = requestAnimationFrame(animate);
  }, [lerp, updateDepthEffect]);

  useEffect(() => {
    createCarousel();

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(animate);

    const handleMouseMove = (e) => {
      if (!isPointerDownRef.current) return;
      getPosX(e.clientX);
    };

    const handleMouseUp = () => {
      if (!pointerStartedInsideRef.current) return;
      endInteraction();
    };

    const handleTouchMove = (e) => {
      if (!isPointerDownRef.current || !e.touches?.length) return;
      getPosX(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!pointerStartedInsideRef.current) return;
      endInteraction();
    };

    const handleResize = () => {
      createCarousel();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("resize", handleResize);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
      }
    };
  }, [animate, createCarousel, getPosX, endInteraction, cards.length]);

  if (!cards.length) return null;

  return (
    <section className="usecases-section page-section" id="use-cases">
      <div className="usecases-head">
        {useCasesDict.title ? (
          <h2 className="usecases-title">{useCasesDict.title}</h2>
        ) : null}

        {useCasesDict.subtitle ? (
          <p className="usecases-subtitle">{useCasesDict.subtitle}</p>
        ) : null}
      </div>

      <div className="usecases-general" ref={containerRef}>
        <div className="usecases-container">
          <div className="usecases-carousel-wrap" ref={carouselContainerRef}>
            <div
              className="usecases-carousel"
              ref={carouselRef}
              onMouseDown={(e) => startInteraction(e.clientX)}
              onTouchStart={(e) => {
                if (!e.touches?.length) return;
                startInteraction(e.touches[0].clientX);
              }}
            >
              {cards.map((card, index) => (
                <article className="usecases-carousel-item" key={card.id}>
                  <div className="usecases-panel-shell">
                    <div className="usecases-panel-media">
                      {card.image ? (
                        <img
                          src={card.image}
                          alt={card.alt}
                          className="usecases-panel-image"
                          loading="lazy"
                          draggable="false"
                        />
                      ) : (
                        <div
                          className={`usecases-panel-image-fallback usecases-panel-image-fallback-${
                            (index % 6) + 1
                          }`}
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    <div className="usecases-panel-content">
                      {card.title ? (
                        <h3 className="usecases-panel-title">{card.title}</h3>
                      ) : null}

                      {card.text ? (
                        <p className="usecases-panel-text">{card.text}</p>
                      ) : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}